/**
 * Blackout Dates Hooks
 *
 * React Query hooks for managing scheduler blackout date configuration.
 * Provides queries and mutations for CRUD operations on blackout dates.
 *
 * @see /supabase/migrations/20251206000002_create_scheduler_blackout_dates.sql
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  BlackoutDate,
  BlackoutDateFormData,
  BlackoutDatesQueryOptions,
  BlackoutDatesResponse,
  BlackoutDateWithDetails,
} from '../types'

/**
 * Query key factory for blackout dates
 */
const blackoutDatesKeys = {
  all: ['scheduler-blackout-dates'] as const,
  lists: () => [...blackoutDatesKeys.all, 'list'] as const,
  list: (filters: BlackoutDatesQueryOptions) => [...blackoutDatesKeys.lists(), filters] as const,
  detail: (id: string) => [...blackoutDatesKeys.all, 'detail', id] as const,
}

/**
 * Fetch blackout dates from database
 */
async function fetchBlackoutDates(
  options: BlackoutDatesQueryOptions = {}
): Promise<BlackoutDatesResponse> {
  let query = supabase.from('scheduler_blackout_dates').select('*', { count: 'exact' })

  // Apply filters
  if (options.franchise_region !== undefined) {
    if (options.franchise_region === null) {
      // Filter for global blackouts (null franchise_region)
      query = query.is('franchise_region', null)
    } else {
      query = query.eq('franchise_region', options.franchise_region)
    }
  }

  if (options.service_area_id !== undefined) {
    if (options.service_area_id === null) {
      query = query.is('service_area_id', null)
    } else {
      query = query.eq('service_area_id', options.service_area_id)
    }
  }

  if (options.is_active !== undefined) {
    query = query.eq('is_active', options.is_active)
  }

  // Date range filters
  if (options.date_from) {
    query = query.gte('end_date', options.date_from)
  }

  if (options.date_to) {
    query = query.lte('start_date', options.date_to)
  }

  // Apply ordering
  const orderBy = options.orderBy || 'start_date'
  query = query.order(orderBy, { ascending: true })

  const { data, error, count } = await query

  if (error) {
    throw new Error(`Failed to fetch blackout dates: ${error.message}`)
  }

  return {
    dates: (data as BlackoutDate[]) || [],
    totalCount: count || 0,
  }
}

/**
 * Fetch single blackout date by ID with service area details
 */
async function fetchBlackoutDate(id: string): Promise<BlackoutDateWithDetails> {
  const { data, error } = await supabase
    .from('scheduler_blackout_dates')
    .select(
      `
      *,
      service_area:scheduler_service_areas(*)
    `
    )
    .eq('id', id)
    .single()

  if (error) {
    throw new Error(`Failed to fetch blackout date: ${error.message}`)
  }

  if (!data) {
    throw new Error('Blackout date not found')
  }

  return data as BlackoutDateWithDetails
}

/**
 * Create new blackout date
 */
async function createBlackoutDate(formData: BlackoutDateFormData): Promise<BlackoutDate> {
  const { data, error } = await supabase
    .from('scheduler_blackout_dates')
    .insert([formData])
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to create blackout date: ${error.message}`)
  }

  return data as BlackoutDate
}

/**
 * Update existing blackout date
 */
async function updateBlackoutDate(
  id: string,
  formData: Partial<BlackoutDateFormData>
): Promise<BlackoutDate> {
  const { data, error } = await supabase
    .from('scheduler_blackout_dates')
    .update(formData)
    .eq('id', id)
    .select()
    .single()

  if (error) {
    throw new Error(`Failed to update blackout date: ${error.message}`)
  }

  return data as BlackoutDate
}

/**
 * Delete blackout date
 */
async function deleteBlackoutDate(id: string): Promise<void> {
  const { error } = await supabase.from('scheduler_blackout_dates').delete().eq('id', id)

  if (error) {
    throw new Error(`Failed to delete blackout date: ${error.message}`)
  }
}

/**
 * Hook: Query blackout dates
 *
 * @example
 * const { data, isLoading } = useBlackoutDates({ franchise_region: 'hamilton' })
 */
export function useBlackoutDates(options: BlackoutDatesQueryOptions = {}) {
  return useQuery({
    queryKey: blackoutDatesKeys.list(options),
    queryFn: () => fetchBlackoutDates(options),
    staleTime: 60_000, // 1 minute
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook: Query single blackout date
 *
 * @example
 * const { data, isLoading } = useBlackoutDate('uuid-here')
 */
export function useBlackoutDate(id: string | null) {
  return useQuery({
    queryKey: blackoutDatesKeys.detail(id || ''),
    queryFn: () => fetchBlackoutDate(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook: Create blackout date mutation
 *
 * @example
 * const { mutate, isPending } = useCreateBlackoutDate()
 * mutate({ label: 'Holiday', start_date: '2025-12-25', ... })
 */
export function useCreateBlackoutDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createBlackoutDate,
    onSuccess: () => {
      // Invalidate all blackout date queries
      queryClient.invalidateQueries({ queryKey: blackoutDatesKeys.all })
    },
  })
}

/**
 * Hook: Update blackout date mutation
 *
 * @example
 * const { mutate } = useUpdateBlackoutDate()
 * mutate({ id: 'uuid-here', data: { is_active: false } })
 */
export function useUpdateBlackoutDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BlackoutDateFormData> }) =>
      updateBlackoutDate(id, data),
    onSuccess: (updatedDate) => {
      // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: blackoutDatesKeys.lists() })
      // Update specific detail query
      queryClient.setQueryData(blackoutDatesKeys.detail(updatedDate.id), updatedDate)
    },
  })
}

/**
 * Hook: Delete blackout date mutation
 *
 * @example
 * const { mutate } = useDeleteBlackoutDate()
 * mutate('uuid-here')
 */
export function useDeleteBlackoutDate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteBlackoutDate,
    onSuccess: () => {
      // Invalidate all blackout date queries
      queryClient.invalidateQueries({ queryKey: blackoutDatesKeys.all })
    },
  })
}

/**
 * Hook: Fetch upcoming blackout dates
 * Convenience wrapper that filters for active blackouts from today forward
 *
 * @example
 * const { data } = useUpcomingBlackouts('hamilton')
 */
export function useUpcomingBlackouts(franchise?: 'hamilton' | 'northyork') {
  const today = new Date().toISOString().split('T')[0]

  return useBlackoutDates({
    franchise_region: franchise,
    is_active: true,
    date_from: today,
  })
}

/**
 * Hook: Fetch active blackout dates (currently in effect)
 * Returns blackouts where today falls within start_date and end_date
 *
 * @example
 * const { data } = useActiveBlackouts('hamilton')
 */
export function useActiveBlackouts(franchise?: 'hamilton' | 'northyork') {
  const today = new Date().toISOString().split('T')[0]

  return useBlackoutDates({
    franchise_region: franchise,
    is_active: true,
    date_from: today,
    date_to: today,
  })
}

/**
 * Hook: Fetch global blackout dates (affect all franchises)
 * Filters for blackouts with franchise_region = null
 *
 * @example
 * const { data } = useGlobalBlackouts()
 */
export function useGlobalBlackouts() {
  return useBlackoutDates({
    franchise_region: null,
    is_active: true,
  })
}

/**
 * Hook: Fetch blackouts for a specific service area
 *
 * @example
 * const { data } = useServiceAreaBlackouts('uuid-of-service-area')
 */
export function useServiceAreaBlackouts(serviceAreaId: string) {
  return useBlackoutDates({
    service_area_id: serviceAreaId,
    is_active: true,
  })
}
